import React, { useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, PhoneCall, MapPin, ShieldOff, ShieldCheck } from "lucide-react";

export default function Lookup() {
  const [phone, setPhone] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!phone.trim()) return toast.error("Enter a phone number");
    setLoading(true); setResult(null);
    try {
      const { data } = await api.get(`/lookup/${encodeURIComponent(phone)}`);
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Lookup failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="lookup-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Numbers</div>
        <h1 className="text-3xl font-black tracking-tighter">Number Lookup</h1>
        <p className="text-xs text-muted-foreground mt-1">Validate an E.164 phone number and get country + carrier hints before sending.</p>
      </div>

      <Card className="rounded-sm shadow-none">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-3"><Label>Phone (E.164 recommended)</Label><Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+919876543210" className="rounded-sm font-mono" data-testid="lookup-input" onKeyDown={e => { if (e.key === "Enter") run(); }} /></div>
            <div className="flex items-end"><Button onClick={run} disabled={loading} className="rounded-sm w-full gap-2" data-testid="lookup-button"><Search className="h-4 w-4" /> {loading ? "Looking up…" : "Look up"}</Button></div>
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card className="rounded-sm shadow-none" data-testid="lookup-result">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              {result.valid
                ? <Badge variant="outline" className="rounded-sm text-[10px] border-emerald-300 text-emerald-700">VALID</Badge>
                : <Badge variant="outline" className="rounded-sm text-[10px] border-red-300 text-red-700">INVALID</Badge>}
              <span className="font-mono">{result.phone}</span>
            </div>
            {result.valid ? (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> Country</div><div className="font-semibold">{result.country} <span className="text-xs text-muted-foreground font-mono">({result.country_code})</span></div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><PhoneCall className="h-3 w-3" /> Carrier hint</div><div className="font-semibold">{result.carrier_hint || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">DND / Opt-out</div><div className="font-semibold flex items-center gap-1">{result.dnd === true ? <><ShieldOff className="h-3 w-3 text-red-600" /> On DND</> : result.dnd === false ? <><ShieldCheck className="h-3 w-3 text-emerald-600" /> Clean</> : "Not in your contacts"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Available Channels</div><div className="flex gap-1 flex-wrap">{(result.channels || []).map(c => <Badge key={c} variant="outline" className="rounded-sm text-[10px]">{c}</Badge>)}</div></div>
              </div>
            ) : (
              <div className="text-sm text-red-700">{result.reason}</div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
